/**
 * quickFilterRules.js — LUẬT THUẦN của bộ lọc nhanh nhiều hàng trong DataListManager.
 *
 * Mục tiêu: đủ hành vi của `FilterQuick.js` (bộ cũ, dùng trong InputBase) để cấu hình
 * `filterQuickConfigs` của listinfo dán sang chạy như cũ:
 *   - nguồn giá trị: danh sách tĩnh (`options`), mảng bản ghi cứng (`api` là MẢNG), danh mục trên máy
 *     chủ (`api` là mã), riêng `api:"trangthai"` đọc `trangthaiapp` trước rồi mới tới `trangthai`;
 *   - `api:"Date"` / `api:"DateTime"` -> ô chọn ngày thay cho các chip;
 *   - `multiSel` -> chọn NHIỀU giá trị, lọc bằng `$in`;
 *   - `selectAll:false` -> KHÔNG có chip "tất cả", mặc định chọn giá trị đầu tiên;
 *   - `title` -> nhãn nhỏ phía trên hàng lọc; `item.color` -> màu chữ của chip;
 *   - `showCount` (← `counterCode` của bộ cũ) -> đếm số bản ghi trên từng chip;
 *   - `prepareData` / `prepareQuery` (chỉ có khi cấu hình đến từ SCRIPT `options.props`).
 *
 * File này KHÔNG import gì để test node chạy thẳng trên mã nguồn.
 */

/** Hàng lọc này thuộc kiểu nào. */
function quickFilterKind(cfg) {
	if (!cfg) return 'none';
	if (cfg.api === 'Date' || cfg.api === 'DateTime') return 'date';
	if (Array.isArray(cfg.options) && cfg.options.length) return 'static';
	if (Array.isArray(cfg.api)) return 'items';
	if (typeof cfg.api === 'string' && cfg.api.trim()) return 'api';
	return 'none';
}

/**
 * Cột giá trị / cột nhãn trong DANH MỤC nạp về.
 * `apiMapping` khai tường minh thắng; không khai thì theo đúng bộ cũ (`field_ref || field`), cuối cùng
 * mới tới mặc định lịch sử của DataListManager (`id` / `name`).
 */
function quickFilterMapping(cfg) {
	const m = (cfg && cfg.apiMapping) || {};
	const value = m.value || (cfg && cfg.field_ref) || 'id';
	const label = m.label || (cfg && cfg.field_label) || m.value || (cfg && cfg.field_ref) || 'name';
	return { value, label };
}

/** Bản ghi danh mục -> các chip `{ value, label, color, raw }`. Bỏ bản ghi không có giá trị. */
function mapQuickFilterItems(items, cfg) {
	const { value, label } = quickFilterMapping(cfg);
	const out = [];
	for (const it of (Array.isArray(items) ? items : [])) {
		if (!it || typeof it !== 'object') continue;
		const v = it[value];
		if (v === undefined || v === null || v === '') continue;
		const l = it[label];
		out.push({ value: v, label: (l === undefined || l === null || l === '') ? String(v) : l, color: it.color, raw: it });
	}
	return out;
}

/** Danh sách chip đang có: tĩnh thì lấy `options`, mảng cứng thì dịch, API thì dùng `apiItems`. */
function quickFilterOptions(cfg, apiItems) {
	const kind = quickFilterKind(cfg);
	let list;
	if (kind === 'static') list = cfg.options.map(o => ({ value: o.value, label: o.label === undefined ? String(o.value) : o.label, color: o.color, raw: o }));
	else if (kind === 'items') list = mapQuickFilterItems(cfg.api, cfg);
	else if (kind === 'api') list = mapQuickFilterItems(apiItems, cfg);
	else list = [];
	if (typeof cfg.prepareData === 'function') {
		try {
			const raws = cfg.prepareData(list.map(o => o.raw));
			if (Array.isArray(raws)) list = kind === 'static' ? raws.map(o => ({ value: o.value, label: o.label, color: o.color, raw: o })) : mapQuickFilterItems(raws, cfg);
		} catch (e) {
			console.error('[quickFilterRules] prepareData lỗi:', (e && e.message) || e);
		}
	}
	return list;
}

/** Hàng lọc đang ở trạng thái "tất cả" (không lọc)? */
function quickFilterIsEmpty(raw) {
	return raw === '' || raw === undefined || raw === null || (Array.isArray(raw) && raw.length === 0);
}

/** Bấm một chip: trả giá trị MỚI của hàng lọc (chọn một, hoặc bật/tắt trong chế độ chọn nhiều). */
function toggleQuickFilterValue(cfg, current, value) {
	if (!cfg || !cfg.multiSel) return value;
	if (value === '' || value === undefined || value === null) return [];   // chip "tất cả"
	const arr = Array.isArray(current) ? current.slice() : (quickFilterIsEmpty(current) ? [] : [current]);
	const i = arr.findIndex(v => String(v) === String(value));
	if (i >= 0) arr.splice(i, 1); else arr.push(value);
	return arr;
}

/** Chip này có đang được chọn không. */
function quickFilterIsActive(cfg, current, value) {
	if (cfg && cfg.multiSel) {
		const arr = Array.isArray(current) ? current : (quickFilterIsEmpty(current) ? [] : [current]);
		return arr.some(v => String(v) === String(value));
	}
	return !quickFilterIsEmpty(current) && String(current) === String(value);
}

/**
 * `selectAll:false` mà chưa có giá trị -> chọn sẵn chip đầu (bộ cũ làm y như vậy khi nạp xong danh mục).
 * Trả `undefined` khi không cần đổi gì.
 */
function quickFilterAutoPick(cfg, current, options) {
	if (!cfg || cfg.selectAll !== false) return undefined;
	if (!quickFilterIsEmpty(current)) {
		// giá trị hiện có không còn trong danh mục -> về chip đầu
		const vals = Array.isArray(current) ? current : [current];
		if (!options.length || vals.every(v => options.some(o => String(o.value) === String(v)))) return undefined;
	}
	if (!options.length) return undefined;
	return cfg.multiSel ? [options[0].value] : options[0].value;
}

/** Khoảng một ngày theo giờ địa phương (lọc Date theo NGÀY chứ không so khớp đúng mili giây). */
function dayRange(v) {
	// 'YYYY-MM-DD' (ô chọn ngày của trình duyệt) phải hiểu là ngày ĐỊA PHƯƠNG — `new Date('2026-09-29')`
	// là nửa đêm UTC, ở múi giờ âm sẽ lùi sang ngày hôm trước.
	const m = typeof v === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
	const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : (v instanceof Date ? v : new Date(v));
	if (isNaN(d.getTime())) return null;
	const from = new Date(d.getFullYear(), d.getMonth(), d.getDate());
	const to = new Date(from.getTime() + 24 * 3600 * 1000 - 1);
	return { $gte: from.toISOString(), $lte: to.toISOString() };
}

/**
 * Điều kiện lọc của MỘT hàng. `null` khi hàng đang ở "tất cả".
 * @param resolveValue  giá trị chip -> giá trị đem lọc (listinfoProps.quickFilterValue: "true"/"false",
 *                      và chuỗi CÓ `return ` là script)
 * @param options       danh sách chip hiện có — cần cho `prepareQuery` (bộ cũ đưa BẢN GHI chứ không phải giá trị)
 */
function quickFilterCondition(cfg, raw, resolveValue, options) {
	if (!cfg || !cfg.field || quickFilterIsEmpty(raw)) return null;
	const resolve = typeof resolveValue === 'function' ? resolveValue : (v => v);
	const kind = quickFilterKind(cfg);
	if (kind === 'date') {
		if (cfg.api === 'DateTime') return { [cfg.field]: raw };
		const r = dayRange(raw);
		return r ? { [cfg.field]: r } : null;
	}
	if (typeof cfg.prepareQuery === 'function') {
		const find = v => ((options || []).find(o => String(o.value) === String(v)) || {}).raw || { [quickFilterMapping(cfg).value]: v };
		try {
			const q = cfg.prepareQuery(Array.isArray(raw) ? raw.map(find).map(r => ({ ...r, sel: true })) : find(raw));
			if (q && typeof q === 'object') return q;
		} catch (e) {
			console.error('[quickFilterRules] prepareQuery lỗi:', (e && e.message) || e);
		}
	}
	if (Array.isArray(raw)) return { [cfg.field]: { $in: raw.map(resolve) } };
	return { [cfg.field]: resolve(raw) };
}

/** Gộp nhiều điều kiện thành MỘT (bỏ phần rỗng). */
function combineConditions(parts) {
	const list = (parts || []).filter(p => p && typeof p === 'object' && Object.keys(p).length);
	if (!list.length) return {};
	return list.length === 1 ? list[0] : { $and: list };
}

/**
 * ĐỒNG BỘ bộ lọc nhanh THEO điều kiện tìm kiếm/lọc nâng cao (đúng `_syncFilterQuickFromCondition` của
 * InputBase): điều kiện trên đúng cột mà một hàng lọc nhanh đang quản lý thì CHUYỂN sang hàng đó — nút
 * sáng đúng giá trị, và hai nơi không cùng lúc ép hai điều kiện ngược nhau lên một cột (danh sách rỗng
 * mà không ai hiểu vì sao).
 * Chỉ nhận giá trị ĐƠN (chuỗi/số/boolean) và `{$in:[...]}` với hàng chọn nhiều; điều kiện phức tạp hơn
 * giữ nguyên ở chỗ cũ.
 * @returns {{ values: object, rest: object, changed: boolean }}
 */
function syncQuickFiltersFromCondition(quickFilters, cond, current) {
	const values = { ...(current || {}) };
	const rest = { ...(cond || {}) };
	let changed = false;
	for (const qf of (quickFilters || [])) {
		if (!qf || !qf.field || !Object.prototype.hasOwnProperty.call(rest, qf.field)) continue;
		if (quickFilterKind(qf) === 'date') continue;
		const v = rest[qf.field];
		const scalar = ['string', 'number', 'boolean'].indexOf(typeof v) >= 0;
		let next;
		if (scalar) next = qf.multiSel ? [v] : v;
		else if (qf.multiSel && v && Array.isArray(v.$in)) next = v.$in.slice();
		else if (Array.isArray(v) && qf.multiSel) next = v.slice();
		else continue;
		values[qf.field] = next;
		delete rest[qf.field];
		changed = true;
	}
	return { values, rest, changed };
}

export {
	quickFilterKind, quickFilterMapping, mapQuickFilterItems, quickFilterOptions, quickFilterIsEmpty,
	toggleQuickFilterValue, quickFilterIsActive, quickFilterAutoPick, dayRange, quickFilterCondition,
	combineConditions, syncQuickFiltersFromCondition,
};
